// 帳號 VMS 儲存版面（MV.3，docs/mycam/mv-spec.md ②）：存在 /v3/me/preferences 的 vms_layouts（不新增資料表）。
// 偏好是整筆取代的 blob → 每次寫都「重讀 → 改 vms_layouts → 寫回」，其他鍵原樣保留。
// 版面只記攝影機清單；格數照各頁規則依台數自動排（不做固定格數 / 大格 / 輪巡）。
import { apiFetch, type UserPreferences } from '../api';

async function readPrefs(): Promise<UserPreferences> {
  const res = await apiFetch<UserPreferences>('/v3/me/preferences');
  return res.data ?? {};
}

export interface VmsLayout {
  id: string;
  name: string;
  devices: string[];
  updated_at_us: number;
}

export const LAYOUT_MAX = 20;
export const LAYOUT_NAME_MAX = 40;
export const LAYOUT_DEVICES_MAX = 16;

export class LayoutError extends Error {
  constructor(public code: 'name' | 'devices' | 'full' | 'notFound') {
    super(code);
  }
}

/** 偏好裡的 vms_layouts → 合法的版面（壞資料略過，不讓整頁壞掉）。 */
export function parseLayouts(prefs: UserPreferences): VmsLayout[] {
  const raw = prefs.vms_layouts;
  if (!Array.isArray(raw)) return [];
  const out: VmsLayout[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    if (typeof o.id !== 'string' || typeof o.name !== 'string' || !Array.isArray(o.devices)) continue;
    const devices = o.devices.filter((d): d is string => typeof d === 'string');
    out.push({ id: o.id, name: o.name, devices, updated_at_us: typeof o.updated_at_us === 'number' ? o.updated_at_us : 0 });
  }
  return out;
}

export function cleanName(name: string): string {
  const n = name.trim();
  if (n.length < 1 || [...n].length > LAYOUT_NAME_MAX) throw new LayoutError('name');
  return n;
}

function cleanDevices(devices: string[]): string[] {
  const d = [...new Set(devices)];
  if (d.length < 1 || d.length > LAYOUT_DEVICES_MAX) throw new LayoutError('devices');
  return d;
}

export function newLayoutId(): string {
  const r = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID().replace(/-/g, '') : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return `lay-${r.slice(0, 26)}`;
}

/** 新增；同名就覆寫那一組（使用者「再存一次」的直覺）。 */
export function addLayout(ls: VmsLayout[], name: string, devices: string[], nowUs: number, id = newLayoutId()): VmsLayout[] {
  const n = cleanName(name);
  const d = cleanDevices(devices);
  const same = ls.find((l) => l.name === n);
  if (same) return ls.map((l) => (l.id === same.id ? { ...l, devices: d, updated_at_us: nowUs } : l));
  if (ls.length >= LAYOUT_MAX) throw new LayoutError('full');
  return [...ls, { id, name: n, devices: d, updated_at_us: nowUs }];
}

export function renameLayout(ls: VmsLayout[], id: string, name: string, nowUs: number): VmsLayout[] {
  const n = cleanName(name);
  if (!ls.some((l) => l.id === id)) throw new LayoutError('notFound');
  return ls.map((l) => (l.id === id ? { ...l, name: n, updated_at_us: nowUs } : l));
}

export function deleteLayout(ls: VmsLayout[], id: string): VmsLayout[] {
  return ls.filter((l) => l.id !== id);
}

/** 拿掉帳號下已經沒有的設備（被移除 / 取消分享）；一台都不剩的版面整組拿掉。changed = 有沒有動到。 */
export function pruneLayouts(ls: VmsLayout[], accountDevices: Set<string>): { layouts: VmsLayout[]; changed: boolean } {
  let changed = false;
  const out: VmsLayout[] = [];
  for (const l of ls) {
    const d = l.devices.filter((x) => accountDevices.has(x));
    if (d.length !== l.devices.length) changed = true;
    if (d.length === 0) continue;
    out.push(d.length === l.devices.length ? l : { ...l, devices: d });
  }
  return { layouts: out, changed };
}

/** 開版面：帳號下還在的 → 這頁開得了的（在線等）→ 取前 max 台。回 removed（帳號下沒了）/ skipped（開不了或超過上限）台數。 */
export function openPlan(layout: VmsLayout, accountDevices: Set<string>, openable: Set<string>, max: number) {
  const present = layout.devices.filter((d) => accountDevices.has(d));
  const can = present.filter((d) => openable.has(d));
  const ids = can.slice(0, max);
  return { ids, removed: layout.devices.length - present.length, skipped: present.length - ids.length };
}

export async function loadLayouts(): Promise<VmsLayout[]> {
  return parseLayouts(await readPrefs());
}

/** 重讀偏好 → 套用 change → 寫回（保留其他鍵）；回傳寫入後的版面。 */
export async function updateLayouts(change: (ls: VmsLayout[]) => VmsLayout[]): Promise<VmsLayout[]> {
  const prefs = await readPrefs();
  const next = change(parseLayouts(prefs));
  await apiFetch('/v3/me/preferences', { method: 'PUT', body: { ...prefs, vms_layouts: next }, idempotencyKey: newLayoutId().replace('lay-', 'idem-') });
  return next;
}
